/**
 * A ROUTE'S LAZY CHUNK FAILED TO LOAD: the browser is holding an older build.
 *
 * Shared by App's error view (apps/app/src/views/Error/Error.tsx) and Book's
 * RouteError (rc.9): the realistic trigger is a redeploy. A page holding a
 * stale index.html requests an old hashed chunk, the SPA catch-all answers
 * with index.html at HTTP 200 as text/html, and `import()` rejects on the MIME
 * type. Moved here from App unchanged.
 */
/**
 * There is no structured signal for "a dynamic import failed" — the rejection
 * is a plain Error and the ONLY discriminator is its message, which every
 * engine words differently. So this is a union of observed phrasings, each
 * attributed, because an unattributed regex is impossible to audit later.
 *
 *   Chromium  "Failed to fetch dynamically imported module: <url>"
 *   Chromium  "Failed to load module script: Expected a JavaScript-or-Wasm
 *              module script but the server responded with a MIME type of
 *              \"text/html\"."
 *   WebKit    "'text/html' is not a valid JavaScript MIME type for module
 *              script '<url>'."          <-- shares NO wording with Chromium
 *   WebKit    "Importing a module script failed."
 *   Firefox   "error loading dynamically imported module"
 *
 * The WebKit MIME phrasing is why this list exists. The previous version
 * matched only the first, second and fifth, so on Safari and every iOS browser
 * a stale chunk fell through to the generic branch and rendered
 * "404 Page not found" — the exact outcome the comment below calls actively
 * misleading, on the exact route it calls out. Verified in both engines.
 *
 * Every alternative above is attributed to an engine on purpose. A bare
 * `module script` catch-all would subsume two of them, but it is exactly the
 * kind of unattributed pattern that makes a regex unauditable later — and
 * apps/book/src/lib/moduleLoadFailure.test.ts covers all five phrasings plus
 * the ordinary errors that must not match, so the floor is asserted rather
 * than assumed.
 *
 * If you add an engine, add its phrasing here AND a row to that table.
 */
export const MODULE_LOAD_FAILURE =
  /dynamically imported module|importing a module script failed|failed to fetch dynamically|failed to load module script|not a valid javascript mime type/i;

/**
 * A LAZY MODULE THAT NEVER SETTLED (rc.13, the Web Director): a hung request
 * leaves a skeleton up forever, since no engine rejects it. Lura's own
 * structured signal, not an engine phrasing: `withChunkTimeout` rejects with
 * this after its wait, and it counts as a module-load failure (Book's
 * RouteError and the Spanish loader then ask build-info, the rc.12 rule).
 */
export class ChunkTimeoutError extends Error {
  constructor(ms: number) {
    super(`A lazily loaded module did not load within ${ms} ms`);
    this.name = "ChunkTimeoutError";
  }
}

/** `load`, or a ChunkTimeoutError after `ms` if it has not settled. */
export function withChunkTimeout<T>(load: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new ChunkTimeoutError(ms)), ms);
    load.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export function isModuleLoadFailure(error: unknown): boolean {
  if (!(error instanceof globalThis.Error)) return false;
  if (error.name === "ChunkTimeoutError") return true;
  return MODULE_LOAD_FAILURE.test(error.message);
}
